import { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";

import { IcdDropdown } from "features/consultations/components/ConsultationEdit/components/IcdDropdown/IcdDropdown";
import { useICD10Codes } from "hooks/useICD10Codes";

import s from "./CdmConditionSelect.module.css";

const MIN_SEARCH_LENGTH = 3;

// Multi-select ICD-10 condition picker: a typeahead (reusing the consultation
// IcdDropdown + useICD10Codes) plus a removable chip per chosen condition.
// `value` is an array of { code, name, icdType }; the parent sends the codes on.
export const CdmConditionSelect = ({ value = [], onChange }) => {
  const [search, setSearch] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  const { data: codes = [], isFetching } = useICD10Codes(
    search.length >= MIN_SEARCH_LENGTH ? search : ""
  );

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const handleSelect = (condition) => {
    if (!value.some((selected) => selected.code === condition.code)) {
      onChange([
        ...value,
        {
          code: condition.code,
          name: condition.name,
          icdType: condition.icdType,
        },
      ]);
    }
    setSearch("");
    setIsOpen(false);
  };

  const handleRemove = (code) =>
    onChange(value.filter((selected) => selected.code !== code));

  return (
    <div className={s.wrapper} ref={containerRef}>
      <div className={s.inputWrapper}>
        <input
          type="text"
          className={s.input}
          value={search}
          placeholder="Search conditions by ICD-10 code or name..."
          onChange={(e) => {
            setSearch(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
        />
        {!!isOpen && !!search && (
          <IcdDropdown
            search={search}
            codes={codes}
            isLoading={isFetching}
            onSelect={handleSelect}
          />
        )}
      </div>
      {value.length > 0 && (
        <div className={s.chips}>
          {value.map((condition) => (
            <span key={condition.code} className={s.chip}>
              <span className={s.chipCode}>{condition.code}</span>
              {!!condition.name && (
                <span className={s.chipName}>{condition.name}</span>
              )}
              <button
                type="button"
                className={s.chipRemove}
                onClick={() => handleRemove(condition.code)}
                aria-label={`Remove ${condition.code}`}
              >
                <X size={14} />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};
